/**
 * Hands a blob to the browser as a download.
 *
 * Lives here rather than in a screen because it is the same three lines every
 * export needs, and because the object URL must be revoked: a lab machine that
 * exports the archive twice a day for a year would otherwise hold every one of
 * those blobs until the tab is closed.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** A phone or a tablet: a finger, not a mouse. */
const TOUCH = '(pointer: coarse)';

/**
 * Whether this device takes a file through its share sheet. The request of
 * 5 October 2026 (third), section 6.
 *
 * An installed iPhone app cannot be relied on to save a blob from a download
 * link, and its share sheet is where "Save Image" lives. A desk browser can
 * share files too, and there a plain download is what somebody expects, so
 * this is asked only of a touch device.
 */
export function sharesFiles(file: File): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return false;
  if (typeof window.matchMedia !== 'function' || !window.matchMedia(TOUCH).matches) return false;
  try {
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
}

/** Shared; closed by the person without choosing; or refused by the browser. */
export type ShareOutcome = 'shared' | 'cancelled' | 'refused';

/**
 * Hands a file to the share sheet. Never throws: closing the sheet is an
 * answer and not a fault, and a refusal (Safari refuses a share that does not
 * come straight from a tap) is for the caller to offer again from one.
 */
export async function shareFile(file: File): Promise<ShareOutcome> {
  try {
    await navigator.share({ files: [file] });
    return 'shared';
  } catch (failure) {
    const name = typeof failure === 'object' && failure !== null ? (failure as { name?: unknown }).name : null;
    return name === 'AbortError' ? 'cancelled' : 'refused';
  }
}
